<script lang="ts">
  // Modeled on CompanyCombobox, but the people list is too large to
  // preload wholesale (unlike the small, vertical-scoped companies lookup):
  // typing debounces into a live search against the people list endpoint's
  // ?q= param instead of client-side filtering a fixed pool. `people` is
  // only used to resolve `value`'s display name for free when the caller
  // has already fetched a pool for other purposes (e.g. DealsPage's
  // contactName lookups) — it never drives the dropdown itself. When
  // nothing typed matches an existing person, a final "Create '<name>'..."
  // option opens a small confirm dialog that posts straight to the
  // existing people endpoint (no separate /quick route needed) and, when a
  // company is already selected on the form, pre-associates the new person
  // with it.
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronDown, Plus } from 'lucide-svelte';
  import { peopleApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import Modal from './Modal.svelte';
  import type { Person } from '$lib/types';

  let {
    value = $bindable(undefined),
    people = [],
    verticalId,
    companyId,
    id,
    placeholder = 'Search or add a person…',
    showVertical = false
  }: {
    value: number | undefined;
    // A pool the caller already fetched (e.g. DealsPage's peopleQuery) —
    // used only to resolve an externally-set `value` to a display name
    // without an extra round trip.
    people?: Person[];
    verticalId?: number;
    companyId?: number;
    id?: string;
    placeholder?: string;
    // Opt-in: shows each result's vertical as a small badge (mapped via
    // session.me.verticals, the same lookup used across the header/admin
    // pages) — off by default so other PersonCombobox callers are unchanged.
    showVertical?: boolean;
  } = $props();

  const queryClient = useQueryClient();

  function personName(p: Pick<Person, 'first_name' | 'last_name'>): string {
    return `${p.first_name} ${p.last_name}`.trim();
  }

  function verticalName(verticalId: number): string {
    return session.me?.verticals.find((v) => v.id === verticalId)?.name ?? '—';
  }

  let query = $state('');
  let open = $state(false);
  // Syncs the visible text from an externally-set `value` (edit mode) once
  // the caller's people pool is available, then never runs again — pick()/
  // the quick-create flow own `query` after that.
  let didInit = false;
  $effect(() => {
    if (didInit) return;
    const v = value;
    const selected = people.find((p) => p.id === v);
    if (v !== undefined && !selected) return;
    query = selected ? personName(selected) : '';
    didInit = true;
  });

  const trimmedQuery = $derived(query.trim());
  let debouncedQuery = $state('');
  $effect(() => {
    const q = trimmedQuery;
    const handle = setTimeout(() => (debouncedQuery = q), 250);
    return () => clearTimeout(handle);
  });

  const searchQuery = createQuery(() => ({
    queryKey: ['people-search', { q: debouncedQuery, verticalId }],
    queryFn: () => peopleApi.list({ q: debouncedQuery || undefined, per_page: 8, vertical_id: verticalId }),
    enabled: open
  }));
  const results = $derived(searchQuery.data?.results ?? []);
  const hasExactMatch = $derived(
    results.some((p) => personName(p).toLowerCase() === trimmedQuery.toLowerCase())
  );
  const showCreateOption = $derived(trimmedQuery.length > 0 && !hasExactMatch);

  function pick(p: Person) {
    value = p.id;
    query = personName(p);
    open = false;
  }

  function clear() {
    value = undefined;
    query = '';
    open = false;
  }

  function onBlur() {
    // Delayed so a mousedown on a dropdown option (or the create button)
    // registers before the dropdown is torn down.
    setTimeout(() => {
      open = false;
      const selected = results.find((p) => p.id === value) ?? people.find((p) => p.id === value);
      if (selected) query = personName(selected);
      else if (value === undefined) query = '';
    }, 120);
  }

  // Same normalization as CompanyCombobox's titleCaseWords: upper-cases
  // each word's first letter, never lower-cases anything, so an
  // already-capitalized name survives.
  function titleCaseWords(s: string): string {
    return s
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0].toUpperCase() + w.slice(1))
      .join(' ');
  }

  let showCreateDialog = $state(false);
  let createFirstName = $state('');
  let createLastName = $state('');
  let createPhone = $state('');
  let createError = $state('');

  function openCreateDialog() {
    const parts = trimmedQuery.split(/\s+/).filter(Boolean);
    createFirstName = parts[0] ?? '';
    createLastName = parts.slice(1).join(' ');
    createPhone = '';
    createError = '';
    showCreateDialog = true;
  }

  function normalizeCreateName() {
    createFirstName = titleCaseWords(createFirstName);
    createLastName = titleCaseWords(createLastName);
  }

  const quickCreateMutation = createMutation(() => ({
    mutationFn: () =>
      peopleApi.create({
        vertical_id: verticalId,
        first_name: createFirstName.trim(),
        last_name: createLastName.trim(),
        phone: createPhone.trim() || undefined,
        company_id: companyId
      }),
    onSuccess: (person) => {
      queryClient.invalidateQueries({ queryKey: ['people-search'] });
      queryClient.invalidateQueries({ queryKey: ['people-picker'] });
      value = person.id;
      query = personName(person);
      showCreateDialog = false;
      open = false;
    },
    onError: (err: unknown) => (createError = getErrorMessage(err))
  }));

  function confirmCreate(e: SubmitEvent) {
    e.preventDefault();
    if (!createFirstName.trim() && !createLastName.trim()) {
      createError = 'Name is required';
      return;
    }
    createError = '';
    quickCreateMutation.mutate();
  }
</script>

<div class="relative">
  <div class="relative">
    <input
      {id}
      class="input pr-8"
      bind:value={query}
      {placeholder}
      autocomplete="off"
      onfocus={() => (open = true)}
      onblur={onBlur}
    />
    <ChevronDown class="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-dark-gray" />
  </div>
  {#if open}
    <div
      class="absolute left-0 right-0 top-full z-30 mt-1.5 max-h-72 overflow-y-auto rounded-[11px] border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
    >
      {#if value !== undefined}
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
          onmousedown={clear}
        >
          <span class="flex-1 text-[13px] font-semibold text-navy">-- none --</span>
        </button>
      {/if}
      {#each results as p (p.id)}
        <!-- svelte-ignore a11y_mouse_events_have_key_events -->
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
          onmousedown={() => pick(p)}
        >
          <span class="flex-1 truncate text-[13px] font-medium text-navy">{personName(p) || '(no name)'}</span>
          {#if p.job_title}
            <span class="shrink-0 truncate text-[11px] text-dark-gray">{p.job_title}</span>
          {/if}
          {#if showVertical}
            <span class="tym-badge shrink-0" style="background:#E6E9EE; color:#10223A">{verticalName(p.vertical_id)}</span>
          {/if}
        </button>
      {/each}
      {#if results.length === 0 && !showCreateOption}
        <div class="px-2.5 py-3.5 text-center text-[12.5px] text-dark-gray">No matches.</div>
      {/if}
      {#if showCreateOption}
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-toyama-teal hover:bg-toyama-light-carbon"
          onmousedown={openCreateDialog}
        >
          <Plus class="h-3.5 w-3.5 shrink-0" />
          <span class="flex-1 truncate text-[13px] font-semibold">Create &quot;{trimmedQuery}&quot;&hellip;</span>
        </button>
      {/if}
    </div>
  {/if}
</div>

{#if showCreateDialog}
  <Modal title="Add person" onClose={() => (showCreateDialog = false)}>
    <form class="space-y-4" onsubmit={confirmCreate}>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="quick-person-first-name">First name</label>
          <input
            id="quick-person-first-name"
            class="input"
            bind:value={createFirstName}
            onblur={normalizeCreateName}
          />
        </div>
        <div>
          <label class="label" for="quick-person-last-name">Last name</label>
          <input
            id="quick-person-last-name"
            class="input"
            bind:value={createLastName}
            onblur={normalizeCreateName}
          />
        </div>
      </div>
      <div>
        <label class="label" for="quick-person-phone">Phone</label>
        <input id="quick-person-phone" class="input" bind:value={createPhone} />
      </div>
      {#if createError}<p class="text-sm text-red-600">{createError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (showCreateDialog = false)}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={quickCreateMutation.isPending}>
          {quickCreateMutation.isPending ? 'Adding...' : 'Confirm'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
